import React, { useState } from 'react';
import { KeyIcon, SparklesIcon, AlertTriangleIcon, XCircleIcon } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(''); // 'error', 'warning', 'terminated', 'removed'
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token.trim()) {
      setError('Please enter a valid token');
      setErrorType('error');
      return;
    }

    setLoading(true);
    setError('');
    setErrorType('');

    try {
      console.log('[LOGIN] Testing token validity...');
      
      // Validate the token by checking usage endpoint
      const response = await fetch('/api/usage', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[LOGIN] Token validation response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[LOGIN] User data:', data);
        
        // Check if user has any prompts left
        if (data.usage.remaining === 0 && data.usage.maxPrompts > 0) {
          setError(`You have used all ${data.usage.maxPrompts} of your available prompts. Please contact support for more prompts.`);
          setErrorType('warning');
          setLoading(false);
          return;
        }
        
        // Check if token is expired
        if (data.token.isExpired) {
          setError(`Your token expired on ${new Date(data.token.expires).toLocaleDateString()}. Please contact support for a new token.`);
          setErrorType('warning');
          setLoading(false);
          return;
        }
        
        console.log('[LOGIN] Token is valid, logging in');
        onLogin(token);
      } else {
        const data = await response.json();
        console.log('[LOGIN] Token validation failed:', data);
        
        // Handle different error types
        if (data.code === 'ACCOUNT_TERMINATED') {
          setError('Your account has been terminated and you can no longer access NeuroForge. Please contact support if you believe this is an error.');
          setErrorType('terminated');
        } else if (data.code === 'ACCOUNT_REMOVED') {
          setError('Your account has been removed from the system. Please contact support if you believe this is an error.');
          setErrorType('removed');
        } else if (data.code === 'TOKEN_EXPIRED') {
          setError('Your token has expired. Please contact support for a new token.');
          setErrorType('warning');
        } else if (data.code === 'INVALID_TOKEN') {
          setError('Invalid token. Please check your token and try again.');
          setErrorType('error');
        } else if (data.code === 'NO_TOKEN') {
          setError('No token provided. Please enter your access token.');
          setErrorType('error');
        } else if (data.code === 'INIT_FAILED') {
          setError('Failed to initialize your account. Please contact support.');
          setErrorType('error');
        } else {
          setError(data.message || data.error || 'Invalid token or account access denied');
          setErrorType('error');
        }
      }
    } catch (error) {
      console.error('[LOGIN] Connection error:', error);
      setError('Connection error. Please check your internet connection and try again.');
      setErrorType('error');
    } finally {
      setLoading(false);
    }
  };

  const getErrorStyles = () => {
    switch (errorType) {
      case 'terminated':
        return 'bg-red-900/30 border-red-600 text-red-300';
      case 'removed':
        return 'bg-red-900/30 border-red-600 text-red-300';
      case 'warning':
        return 'bg-yellow-900/20 border-yellow-600 text-yellow-300';
      default:
        return 'bg-red-900/20 border-red-700 text-red-400';
    }
  };

  const getErrorIcon = () => {
    switch (errorType) {
      case 'terminated':
      case 'removed':
        return <XCircleIcon className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangleIcon className="h-5 w-5" />;
      default:
        return <AlertTriangleIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/NFLogo.png"
                alt="NeuroForge Logo"
                className="h-10 w-10 mr-2 object-contain drop-shadow-lg"
              />
              <h1 className="text-3xl font-bold text-white">NeuroForge</h1>
            </div>
            <p className="text-slate-300 text-sm">
              AI Battle Arena – Where Models Clash & Winners Emerge
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Access Token :
              </label>
              <div className="relative">
                <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="Enter your JWT token"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className={`border rounded-lg p-4 ${getErrorStyles()}`}>
                <div className="flex items-start space-x-3">
                  {getErrorIcon()}
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {errorType === 'terminated' && 'Account Terminated'}
                      {errorType === 'removed' && 'Account Removed'}
                      {errorType === 'warning' && 'Access Restricted'}
                      {errorType === 'error' && 'Authentication Error'}
                    </p>
                    <p className="text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token.trim()}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Validating...
                </span>
              ) : (
                'Enter NeuroForge'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-slate-400 text-xs">
              Don't have a token?{" "}
              <a
                href="https://web-runner.net/neuroforge-demo-access"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
              >
                Get Access
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
